/**
 * @description
 * Client configuration service for managing client-specific configurations.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 */

import { BaseAPIClient } from "../core/base-client";
import type { ClientConfig } from "./types";

/**
 * Client configuration service
 */
export class ClientConfigService extends BaseAPIClient {
  /**
   * Get all configurations for a client
   */
  async getClientConfigs(clientId: string): Promise<ClientConfig[]> {
    const data = await this.request<{ configs: ClientConfig[] }>(
      `/clients/${clientId}/configs`,
    );
    return data.configs;
  }

  /**
   * Get a single client configuration
   */
  async getClientConfig(clientId: string, key: string): Promise<ClientConfig> {
    const data = await this.request<{ config: ClientConfig }>(
      `/clients/${clientId}/configs/${key}`,
    );
    return data.config;
  }

  /**
   * Update or create a client configuration
   */
  async updateClientConfig(
    clientId: string,
    key: string,
    value: string,
    isSecret?: boolean,
    description?: string,
  ): Promise<ClientConfig> {
    const response = await this.request<{ config: ClientConfig }>(
      `/clients/${clientId}/configs/${key}`,
      {
        method: "PUT",
        body: JSON.stringify({ value, isSecret, description }),
      },
    );
    return response.config;
  }

  /**
   * Delete a client configuration
   */
  async deleteClientConfig(clientId: string, key: string): Promise<void> {
    await this.request(`/clients/${clientId}/configs/${key}`, {
      method: "DELETE",
    });
  }

  /**
   * Bulk update client configurations
   */
  async bulkUpdateClientConfigs(
    clientId: string,
    configs: Array<{
      key: string;
      value: string;
      isSecret?: boolean;
      description?: string;
    }>,
  ): Promise<{ success: boolean; updated: number }> {
    return this.request<{ success: boolean; updated: number }>(
      `/clients/${clientId}/configs/bulk`,
      {
        method: "POST",
        body: JSON.stringify({ configs }),
      },
    );
  }

  /**
   * Initialize default VibeSDK configurations for a client
   */
  async initializeClientConfigs(
    clientId: string,
  ): Promise<{ success: boolean; initialized: number }> {
    return this.request<{ success: boolean; initialized: number }>(
      `/clients/${clientId}/configs/initialize`,
      {
        method: "POST",
      },
    );
  }
}

// Export singleton instance
export const clientConfigService = new ClientConfigService();
